import { cn } from "@/utils";
import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { FieldShell } from "./Field";
import { controlBase } from "./field.styles";
import { useLarguraDoAfixo } from "./useLarguraDoAfixo";

/**
 * Campo numérico com formatação pt-BR (1.234,56).
 *
 * Por que não é um `<input type="number">`: ele traz setinhas que ninguém usa,
 * muda de valor quando a roda do mouse passa por cima, e o separador decimal
 * varia com a configuração do sistema operacional — num campo de dinheiro isso
 * é convite a erro de digitação caro. Aqui é `type="text"` com `inputMode`, que
 * abre o teclado numérico no celular sem nenhum desses efeitos.
 *
 * O modelo de digitação é o de **centavos**, o mesmo dos aplicativos de banco:
 * só dígitos entram, e cada tecla empurra o valor uma casa. Digitar 1-2-3-4-5-6
 * vira 1.234,56. Isso resolve de uma vez os dois problemas clássicos de campo
 * de dinheiro — o cursor que pula quando o separador de milhar aparece, e o
 * usuário sem saber se digita vírgula ou ponto.
 */

type NumberFieldOwnProps = {
  label: string;
  hideLabel?: boolean;
  hint?: ReactNode;
  error?: string;
  containerClassName?: string;
  /** Valor em unidades reais: 1234.56 para R$ 1.234,56. */
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number | undefined) => void;
  /** 2 para dinheiro, 0 para contagem. */
  decimals?: number;
  /** Texto fixo à esquerda, como "R$". Decorativo. */
  prefix?: ReactNode;
  /** Texto fixo à direita, como "%". Decorativo. */
  suffix?: ReactNode;
  /** Teto do valor. Digitar acima disso simplesmente não entra. */
  max?: number;
};

export interface NumberFieldProps
  extends Omit<
      InputHTMLAttributes<HTMLInputElement>,
      "value" | "defaultValue" | "onChange" | "type" | "prefix" | "max" | "min"
    >,
    NumberFieldOwnProps {}

const formatar = (valor: number | undefined, decimals: number): string =>
  valor === undefined
    ? ""
    : valor.toLocaleString("pt-BR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

/** Marca "campo esvaziado, falta ver o que o pai devolve no próximo render". */
const AGUARDANDO_PAI = Symbol("aguardando-pai");

/** Lê só os dígitos do que foi digitado ou colado e devolve o valor. */
const digitosParaValor = (
  texto: string,
  decimals: number
): number | undefined => {
  const digitos = texto.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  if (digitos === "") return undefined;
  return Number(digitos) / 10 ** decimals;
};

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  (
    {
      label,
      hideLabel,
      hint,
      error,
      containerClassName,
      className,
      value,
      defaultValue,
      onValueChange,
      decimals = 2,
      prefix,
      suffix,
      max,
      id,
      required,
      placeholder,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const controlId = id ?? generatedId;
    const hintId = `${controlId}-hint`;
    const errorId = `${controlId}-error`;

    const medidaDoPrefixo = useLarguraDoAfixo(prefix);
    const medidaDoSufixo = useLarguraDoAfixo(suffix);

    const controlado = value !== undefined || onValueChange !== undefined;
    const [valorInterno, setValorInterno] = useState<number | undefined>(
      defaultValue
    );
    // Quem apagou o campo espera vê-lo vazio. O `Controller` do react-hook-form
    // não deixa: ao receber `undefined`, ele devolve o `defaultValue` em
    // `value`, e o campo "ressuscita" o número antigo — apagar 9.000 com
    // backspace virava 900, 90, 9 e de novo 9.000, sem nunca chegar ao zero.
    // Guardamos o que o pai devolve logo depois do esvaziamento e mostramos
    // vazio enquanto ele continuar devolvendo isso; se o pai trocar o valor
    // (um `setValue`, um `reset`), o número novo volta a aparecer.
    const [esvaziadoSobre, setEsvaziadoSobre] = useState<{
      valor: number | undefined | typeof AGUARDANDO_PAI;
    } | null>(null);
    const valorRecebido = controlado ? value : valorInterno;
    if (esvaziadoSobre?.valor === AGUARDANDO_PAI) {
      setEsvaziadoSobre({ valor: valorRecebido });
    }
    const campoVazio =
      esvaziadoSobre !== null &&
      (esvaziadoSobre.valor === AGUARDANDO_PAI ||
        Object.is(esvaziadoSobre.valor, valorRecebido));
    const valorAtual = campoVazio ? undefined : valorRecebido;

    const aoDigitar = (texto: string) => {
      let proximo = digitosParaValor(texto, decimals);
      if (proximo !== undefined && max !== undefined && proximo > max) {
        proximo = valorAtual; // acima do teto, ignora a tecla em vez de cortar
      }
      setEsvaziadoSobre(
        proximo === undefined ? { valor: AGUARDANDO_PAI } : null
      );
      if (!controlado) setValorInterno(proximo);
      onValueChange?.(proximo);
    };

    return (
      <FieldShell
        label={label}
        hideLabel={hideLabel}
        hint={hint}
        error={error}
        required={required}
        controlId={controlId}
        hintId={hintId}
        errorId={errorId}
        className={containerClassName}
      >
        <div className="relative flex items-center">
          {prefix ? (
            <span
              ref={medidaDoPrefixo.ref}
              aria-hidden="true"
              className="font-ds-sans text-ds-body text-ds-ink-600 pointer-events-none absolute left-3 whitespace-nowrap"
            >
              {prefix}
            </span>
          ) : null}

          <input
            ref={ref}
            id={controlId}
            type="text"
            // "decimal" abre o teclado com vírgula no iOS; "numeric" só dígitos.
            inputMode={decimals > 0 ? "decimal" : "numeric"}
            // Impede o corretor e a capitalização de mexerem no campo.
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            required={required}
            value={formatar(valorAtual, decimals)}
            placeholder={placeholder ?? formatar(0, decimals)}
            onChange={event => aoDigitar(event.target.value)}
            // O valor é reescrito a cada tecla, então o cursor vai sempre ao
            // fim — é o comportamento de aplicativo de banco, e o único que não
            // embaralha a digitação quando o ponto de milhar aparece.
            onKeyDown={event => {
              if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                event.preventDefault();
              }
            }}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : hint ? hintId : undefined}
            className={cn(controlBase, "ds-numeric min-h-12 px-3", className)}
            // O espaço do afixo é medido, não estimado — e vai no `style`
            // porque a largura só existe em tempo de execução. `paddingLeft` e
            // `paddingRight` vencem o `px-3` da classe por serem inline.
            style={{
              paddingLeft: medidaDoPrefixo.espaco,
              paddingRight: medidaDoSufixo.espaco,
              ...props.style,
            }}
            {...props}
          />

          {suffix ? (
            <span
              ref={medidaDoSufixo.ref}
              aria-hidden="true"
              className="font-ds-sans text-ds-body text-ds-ink-600 pointer-events-none absolute right-3 whitespace-nowrap"
            >
              {suffix}
            </span>
          ) : null}
        </div>
      </FieldShell>
    );
  }
);
NumberField.displayName = "NumberField";
